<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div v-if="filesEditMode == 'Calls'" class="modal__main-icon modal__main-icon--calls"></div>
      <div v-else-if="filesEditMode == 'Pendencies'" class="modal__main-icon modal__main-icon--pendencies"></div>
      <div v-else-if="filesEditMode == 'Notes'" class="modal__main-icon modal__main-icon--notes"></div>
      <div v-else-if="filesEditMode == 'Offers'" class="modal__main-icon modal__main-icon--offer"></div>
      <div v-else-if="filesEditMode == 'Leads'" class="modal__main-icon modal__main-icon--notes"></div> 
      <div v-else-if="filesEditMode == 'Mandates'" class="modal__main-icon modal__main-icon--mandat"></div>

      <div class="modal__close" @click="closeModal()"></div>

      <div class="modal__title">
        {{ filesEditMode }} {{ $t(`allFilesModal.${"files"}`) }}
      </div>

      <Loading v-if="filesLoading" />

      <div class="content" v-else>
        <div class="files">
          <div class="files__header">
            <div class="files__label files__label--large name">Name</div>
            <div class="files__label files__label--medium">{{ $t(`allFilesModal.${"view"}`) }}</div>
            <div class="files__label files__label--medium date">{{ $t(`allFilesModal.${"date"}`) }}</div>
            <div class="files__label files__label--medium download-label">Download</div>
            <div class="files__label files__label--small">{{ $t(`allFilesModal.${"delete"}`) }}</div>
          </div>
          <div class="files__body">
            <File
              v-for="file in filesToEdit"
              :key="file.id"
              :file="file"
              :updateFiles="updateFiles"
            />
          </div>
        </div>
        <DropFile
          @input="uploadFiles($event)"
          :deletable="false"
          :bus="bus"
          :multiple="filesEditMode === 'Leads'"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Loading from "@/partials/Loading";
import File from "@/components/AccountDetails/File";
import DropFile from "@/components/DropFile";

defineOptions({
  name: "AllFilesModal",
});

const store = useStore();
const route = useRoute();

// ─── Event bus for DropFile clearing ──────────────────────────────────────────
// DropFile uses props.bus.on / .off / .emit.
const _listeners = {};
const bus = {
  on(event, handler) {
    if (!_listeners[event]) _listeners[event] = [];
    _listeners[event].push(handler);
  },
  off(event, handler) {
    if (_listeners[event])
      _listeners[event] = _listeners[event].filter((h) => h !== handler);
  },
  emit(event, ...args) {
    (_listeners[event] || []).forEach((h) => h(...args));
  },
};

// ─── State ────────────────────────────────────────────────────────────────────
const files = ref([]);
const filesLoading = ref(false);

// ─── Getters ──────────────────────────────────────────────────────────────────
const filesEditMode = computed(() => store.getters.filesEditMode);
const filesToEdit = computed(() => store.getters.filesToEdit);
const filesToEditId = computed(() => store.getters.filesToEditId);
const filesToEditOffers = computed(() => store.getters.filesToEditOffers);
const filesFromDashboard = computed(() => store.getters.filesFromDashboard);
const customerFromDashboard = computed(() => store.getters.customerFromDashboard);
const offerToEdit = computed(() => store.getters.offerToEdit);
const customerDetails = computed(() => store.getters.customerDetails);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isDashboardRoute = computed(() => route.path === "/dashboard");

// ─── Methods ──────────────────────────────────────────────────────────────────
const fetchSalesData = async () => {
  if (isDashboardRoute.value) {
    try {
      await store.dispatch("getSalesDashboard");
    } catch (error) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: error,
      });
    }
  }
};

const closeModal = () => {
  store.commit("setShowFilesEditModal", false);
  store.commit("setFilesToEditId", null);
  store.commit("setFilesToEdit", []);
  store.commit("setFilesToEditOffers", []);
  store.commit("setFilesEditMode", null);
  store.commit("setFilesFromDashboard", false);
  store.commit("setCustomerFromDashboard", null);
};

const uploadFiles = async (event) => {
  filesLoading.value = true;
  files.value = event;

  const uploadedFiles = files.value.map((file) => file.file);

  try {
    if (filesEditMode.value === "Pendencies") {
      await store.dispatch("addPendencyFile", {
        pendencyId: filesToEditId.value,
        files: uploadedFiles,
      });
    } else if (filesEditMode.value === "Notes") {
      await store.dispatch("addNotesFile", {
        noteId: filesToEditId.value,
        files: uploadedFiles,
      });
    } else if (filesEditMode.value === "Calls") {
      await store.dispatch("addCallsFile", {
        callId: filesToEditId.value,
        files: uploadedFiles,
      });
    } else if (filesEditMode.value === "Offers") {
      await store.dispatch("addOfferFile", {
        offerId: filesToEditId.value,
        files: uploadedFiles,
      });
    } else if (filesEditMode.value === "Leads") {
      await store.dispatch("addLeadFiles", {
        leadId: filesToEditId.value,
        files: uploadedFiles,
      });
    } else if (filesEditMode.value === "Mandates") {
      await store.dispatch("addMandateFiles", {
        mandateId: filesToEditId.value,
        files: uploadedFiles,
      });
    }

    await updateFiles();
    bus.emit("delete");

    store.commit("setToast", {
      display: true,
      type: "success",
      message: "Files uploaded successfully",
    });
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || "Failed to upload files";
    store.commit("setToast", {
      display: true,
      type: "error",
      message: errorMessage,
    });
    console.error("File upload error:", error);
  } finally {
    filesLoading.value = false;
  }
};

const updateFiles = async () => {
  filesLoading.value = true;

  try {
    if (filesEditMode.value === "Pendencies") {
      await store.dispatch("fetchPendencyFiles", filesToEditId.value);

      if (filesToEditOffers.value && filesToEditOffers.value.length) {
        const filesList = [...filesToEdit.value];

        const refreshedOffers = await Promise.all(
          filesToEditOffers.value.map((offer) => {
            if (!offer || !offer.id) return null;
            return store.dispatch("getSpecificOffer", offer.id);
          })
        );

        refreshedOffers.forEach((offer) => {
          if (!offer) return;
          filesList.push({
            id: `offer_${offer.id}`,
            name: `offer_${offer.id}.pdf`,
            file_name: `offer_${offer.id}.pdf`,
            created_at: offer.created_at,
            isDownloadable: true,
            offerId: offer.id,
          });
          if (offer.files && Array.isArray(offer.files)) {
            filesList.push(...offer.files);
          }
        });

        store.commit("setFilesToEdit", filesList);
      }

      await refreshPendencies();
    } else if (filesEditMode.value === "Notes") {
      await store.dispatch("fetchNoteFiles", filesToEditId.value);
      await refreshNotes();
    } else if (filesEditMode.value === "Calls") {
      await store.dispatch("fetchCallsFiles", filesToEditId.value);
      await refreshCalls();
    } else if (filesEditMode.value === "Offers") {
      await refreshOffers();
    } else if (filesEditMode.value === "Leads") {
      await store.dispatch("fetchLeadFiles", filesToEditId.value);
      await refreshLeads();
    } else if (filesEditMode.value === "Mandates") {
      await refreshMandates();
    }
  } catch (error) {
    store.commit("setToast", {
      display: true,
      type: "error",
      message: error.response?.data?.message || error.message || "Failed to update files",
    });
    console.error("Update files error:", error);
  } finally {
    filesLoading.value = false;
  }
};

const refreshPendencies = async () => {
  let customerId = null;

  if (filesFromDashboard.value) {
    customerId = customerFromDashboard.value;
  } else {
    customerId = route.params.id;
  }

  try {
    await store.dispatch("getPendencies", customerId);
  } catch (error) {
    console.error(error);
  }

  await fetchSalesData();

  store.commit("setFilesFromDashboard", false);
  store.commit("setCustomerFromDashboard", null);
};

const refreshNotes = async () => {
  try {
    await store.dispatch("getNotes", route.params.id);
  } catch (error) {
    console.error(error);
  }
};

const refreshCalls = async () => {
  try {
    await store.dispatch("getCalls", route.params.id);
  } catch (error) {
    // silently ignore, matching Vue2
  }
};

const refreshOffers = async () => {
  try {
    const customerId = route.params.id;
    await store.dispatch("getSpecificOffer", filesToEditId.value);
    await store.dispatch("getCustomerOffers", customerId);

    const offer = offerToEdit.value;
    if (offer) {
      const filesList = [];
      filesList.push({
        id: `offer_${offer.id}`,
        name: `offer_${offer.id}.pdf`,
        file_name: `offer_${offer.id}.pdf`,
        created_at: offer.created_at,
        isDownloadable: true,
        offerId: offer.id,
      });
      if (offer.files && Array.isArray(offer.files)) {
        filesList.push(...offer.files);
      }
      store.commit("setFilesToEdit", filesList);
    }
  } catch (error) {
    console.error(error);
  }
};

const refreshLeads = async () => {
  try {
    let customerId = null;
    
    if (filesFromDashboard.value) {
      customerId = customerFromDashboard.value;
    } else if (route.params.id) {
      customerId = route.params.id;
    } else if (customerDetails.value && customerDetails.value.id) {
      customerId = customerDetails.value.id;
    }
    
    if (customerId) {
      await store.dispatch("fetchLeads", { customerId, page: 1 });
    } else {
      await store.dispatch("fetchLeadCustomers");
    }
  } catch (error) {
    console.error(error);
  }
};

const refreshMandates = async () => {
  try {
    let customerId = null;

    if (filesFromDashboard.value) {
      customerId = customerFromDashboard.value;
    } else if (route.params.id) {
      customerId = route.params.id;
    } else if (customerDetails.value && customerDetails.value.id) {
      customerId = customerDetails.value.id;
    }

    if (customerId) {
      const mandateData = await store.dispatch("fetchMandateFiles", {
        customerId,
        mandateId: filesToEditId.value,
      });
      store.commit("setFilesToEdit", mandateData.files || [], { root: true });
    }
  } catch (error) {
    console.error(error);
  }
};

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onUnmounted(() => {
  closeModal();
});
</script>

<style lang="scss" scoped>
.modal {
  &__title {
    margin: 53px 0 35px 10px;
    font-size: var(--medium);
    font-family: var(--muli-text-semi-bold);
  }

  .modal__title,
  .files__label,
  :deep(.file) {
    color: var(--text-2);
  }

  .content {
    display: flex;
    align-items: flex-start;
    height: calc(100% - 53px - 35px - 18px);
  }

  .files {
    width: 100%;
    height: 100%;
    margin-right: 20px;
    font-size: var(--x-small);

    &__header {
      border-bottom: 1px solid var(--border-10);
      display: flex;
      padding: 5px 10px;
      margin-bottom: 8px;
    }

    &__body {
      height: calc(100% - 25px - 8px);
      overflow: auto;
      position: relative;
    }

    :deep(.file) {
      display: flex;
      padding: 20px 10px;
      background: var(--bg-3);
      margin-bottom: 1px;
      position: relative;
      height: 64px;
      max-height: 64px;
      transition: all 0.3s ease;

      .icon {
        background-position: left;
      }
    }

    :deep(.file__data--large) {
      color: var(--text-10);
      text-decoration: underline;
    }

    &__label,
    :deep(.file__data) {
      display: flex;
      align-items: center;
    }

    &__label--large,
    :deep(.file__data--large) {
      flex: 3;
    }

    &__label--medium,
    :deep(.file__data--medium) {
      flex: 2;
    }

    &__label--small,
    :deep(.file__data--small) {
      flex: 1;
    }
  }
}

.files__label {
  justify-content: center !important;
}

.name {
  justify-content: flex-start !important;
}

.date {
  justify-content: flex-start !important;
}
</style>
